import { useUser } from "@/components/UserProvider";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { Input } from "@/components/ui/shadcn/Input/Input";
import { Label } from "@/components/ui/shadcn/Label/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/shadcn/Select/Select";
import { Textarea } from "@/components/ui/shadcn/TextArea/Textarea";
import {
  useCreateContactPhoneCall,
  useUpdateContactPhoneCall,
} from "@/lib/data/react-query";
import type {
  ContactPhoneCall,
  ContactPhoneCallCreateInput,
} from "@/lib/types";
import { useMemo, useState } from "react";
import { ContactPhoneNumberField } from "./ContactPhoneNumberField";
import { PhoneCallParticipantsField } from "./PhoneCallParticipantsField";
import {
  OUTCOME_OPTIONS,
  buildParticipant,
  participantToEditable,
  toDateTimeLocalValue,
  type CallDirection,
  type CallOutcome,
  type EditableParticipant,
} from "./phone-call-log-utils";

type ParticipantInput = ContactPhoneCallCreateInput["participants"][number];

export function CallForm({
  call,
  contactId,
  contactPhoneNumbers,
  onCancel,
  onSaved,
}: {
  call?: ContactPhoneCall;
  contactId: string;
  contactPhoneNumbers: { id: string; label: string; number: string }[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { currentUser } = useUser();
  const createCall = useCreateContactPhoneCall();
  const updateCall = useUpdateContactPhoneCall();
  const now = useMemo(() => new Date(), []);
  const [direction, setDirection] = useState<CallDirection>(
    call?.direction ?? "OUTBOUND",
  );
  const [outcome, setOutcome] = useState<CallOutcome>(
    call?.outcome ?? "COMPLETED",
  );
  const [startedAt, setStartedAt] = useState(
    toDateTimeLocalValue(
      call?.startedAt ?? new Date(now.getTime() - 5 * 60_000),
    ),
  );
  const [endedAt, setEndedAt] = useState(
    toDateTimeLocalValue(call?.endedAt ?? now),
  );
  const [description, setDescription] = useState(call?.description ?? "");
  const existingContactParticipant = call?.participants.find(
    (participant) =>
      participant.kind === "CONTACT" && participant.contactId === contactId,
  );
  const [contactPhoneNumber, setContactPhoneNumber] = useState(
    call
      ? (existingContactParticipant?.phoneNumber ?? "")
      : (contactPhoneNumbers.find((phone) => phone.number)?.number ?? ""),
  );
  const [participants, setParticipants] = useState<EditableParticipant[]>(
    () => {
      const additional =
        call?.participants
          .filter(
            (participant) =>
              !(
                participant.kind === "CONTACT" &&
                participant.contactId === contactId
              ),
          )
          .map(participantToEditable) ?? [];
      return additional.length
        ? additional
        : [
            {
              key: crypto.randomUUID(),
              kind: "USER",
              value: currentUser.id,
              phoneNumber: currentUser.phoneNumber,
            },
          ];
    },
  );
  const [validationError, setValidationError] = useState<string>();
  const pending = createCall.isPending || updateCall.isPending;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setValidationError(undefined);
    const start = new Date(startedAt);
    const end = new Date(endedAt);
    if (
      !startedAt ||
      !endedAt ||
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime())
    ) {
      setValidationError("Enter a valid start and end time.");
      return;
    }
    if (end < start) {
      setValidationError("End time cannot be before start time.");
      return;
    }
    const additionalParticipants = participants.map(buildParticipant);
    if (additionalParticipants.some((participant) => participant == null)) {
      setValidationError("Complete each participant before saving.");
      return;
    }
    const payload: ContactPhoneCallCreateInput = {
      direction,
      outcome,
      startedAt: start.toISOString(),
      endedAt: end.toISOString(),
      description: description.trim() || null,
      participants: [
        {
          kind: "CONTACT",
          contactId,
          ...(contactPhoneNumber ? { phoneNumber: contactPhoneNumber } : {}),
        },
        ...(additionalParticipants as ParticipantInput[]),
      ],
    };
    try {
      if (call) {
        if (!call.dateModified) {
          setValidationError(
            "This call is missing its latest version. Refresh the call history and try again.",
          );
          return;
        }
        await updateCall.mutateAsync({
          id: call.id,
          dateModified: call.dateModified,
          ...payload,
        });
      } else {
        await createCall.mutateAsync(payload);
      }
      onSaved();
    } catch {
      // Mutation hooks display the API error and keep the form open for retry.
    }
  };

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(event) => void submit(event)}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Direction</Label>
          <Select
            value={direction}
            onValueChange={(value) => {
              setDirection(value as CallDirection);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="OUTBOUND">Outgoing</SelectItem>
              <SelectItem value="INBOUND">Incoming</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Outcome</Label>
          <Select
            value={outcome}
            onValueChange={(value) => {
              setOutcome(value as CallOutcome);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OUTCOME_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone-call-started-at">Started at</Label>
          <Input
            id="phone-call-started-at"
            type="datetime-local"
            value={startedAt}
            onChange={(event) => {
              setStartedAt(event.target.value);
            }}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone-call-ended-at">Ended at</Label>
          <Input
            id="phone-call-ended-at"
            type="datetime-local"
            value={endedAt}
            onChange={(event) => {
              setEndedAt(event.target.value);
            }}
            required
          />
        </div>
      </div>
      <ContactPhoneNumberField
        phones={contactPhoneNumbers}
        value={contactPhoneNumber}
        onChange={setContactPhoneNumber}
      />
      <PhoneCallParticipantsField
        contactId={contactId}
        participants={participants}
        setParticipants={setParticipants}
      />
      <div className="space-y-2">
        <Label htmlFor="phone-call-description">Description</Label>
        <Textarea
          id="phone-call-description"
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
          placeholder="Add notes about the call (optional)"
          className="min-h-24"
        />
      </div>
      {validationError ? (
        <p className="text-destructive text-sm" role="alert">
          {validationError}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? <LoadingSpinner /> : null}
          {call ? "Save changes" : "Log call"}
        </Button>
      </div>
    </form>
  );
}
